import {
  Dropdown,
  IDropdownOption,
  IDropdownProps,
  IDropdownStyleProps,
  IDropdownStyles,
  TooltipHost,
} from '@fluentui/react';
import React, { useState } from 'react';
import { FilterArrayType, FilterCombineType } from '.';

type DropdownFilterProps = {
  placeholder?: string;
  options: IDropdownOption[];
  filterKey: string;
  onOptionChange?: (data: FilterArrayType) => void;
  disabled?: boolean;
  selectedKey?: number | string | undefined | null;
  defaultFilter?: boolean;
  controlled?: boolean;
  combineType?:
    | FilterCombineType.AND
    | FilterCombineType.NOT
    | FilterCombineType.OR;
  maxHeight?: number;
  maxWidth?: number;
  multiSelect?: boolean;
  width?: number;
  NoBgColor?: boolean;
} & Pick<
  IDropdownProps,
  'dropdownWidth' | 'defaultSelectedKey' | 'styles' | 'onChange'
>;

export const DropdownFilter: React.FC<DropdownFilterProps> = ({
  placeholder = 'Select item',
  defaultFilter = false,
  onOptionChange,
  options = [],
  filterKey,
  disabled = false,
  selectedKey,
  controlled = false,
  combineType = FilterCombineType.AND,
  maxHeight,
  multiSelect = false,
  NoBgColor = false,
  width,
  maxWidth,
  ...props
}) => {
  const label = defaultFilter ? placeholder : undefined;
  const tooltipContent = defaultFilter ? undefined : placeholder;
  const placeholderContent = defaultFilter ? 'Select' : placeholder;
  const selectedKeyValue = controlled ? selectedKey : undefined;
  const [selectedValues] = useState<Map<string, boolean>>(new Map());
  const [multiSelectedKeys, setMultiSelectedKeys] = useState<string[]>([]);

  const dropdownWidth = width ? width : undefined;
  function getDropdownStyles(
    props: IDropdownStyleProps
  ): Partial<IDropdownStyles> {
    return {
      root: {
        textAlign: 'center',
        border: 0,
        width: dropdownWidth,
        maxWidth: maxWidth ?? undefined,
      },
      label: {
        whiteSpace: 'normal',
      },
      callout: { height: maxHeight },
      title: {
        backgroundColor: NoBgColor
          ? undefined
          : props.theme?.palette.neutralLighter,
        selectors: {
          ':hover': {
            borderRadius: 5,
            backgroundColor: props.theme?.palette.neutralQuaternary,
          },
        },
        border: 0,
      },
    };
  }
  return (
    <TooltipHost
      content={
        multiSelectedKeys.length > 1
          ? multiSelectedKeys.join(',')
          : tooltipContent
      }
    >
      <Dropdown
        dropdownWidth={'auto'}
        disabled={disabled}
        placeholder={placeholderContent}
        options={options}
        multiSelect={multiSelect}
        label={label}
        selectedKey={selectedKeyValue}
        styles={defaultFilter ? undefined : getDropdownStyles}
        onChange={(_, option) => {
          let value: string | number | string[] = option?.key!;
          if (multiSelect && option) {
            if (option.text) {
              // Ensure option and option.text exist
              if (option.selected) {
                selectedValues.set(option?.key as string, true);
                value = Array.from(selectedValues.keys());
                // Add option.text to multiSelectedKeys if not already included
                setMultiSelectedKeys((prev) =>
                  prev.includes(option.text) ? prev : [...prev, option.text]
                );
              } else {
                // Remove the deselected item from the state
                selectedValues.delete(option?.key as string);
                value = Array.from(selectedValues.keys());
                // Remove option.text from multiSelectedKeys
                setMultiSelectedKeys((prev) =>
                  prev.filter((item) => item !== option.text)
                );
              }
            }
          }
          onOptionChange?.({
            value: value,
            filterKey,
            label: option?.text!,
            combineType,
          });
        }}
        {...props}
      />
    </TooltipHost>
  );
};

export function getDefaultDropdownStyles():
  | Partial<IDropdownStyles>
  | undefined {
  return undefined;
}
